<template>
  <SiteNavigation :chair="chair" :pages="pages"/>
  <RouterView v-slot="{ Component }">
    <Transition name="fade-out" mode="out-in">
      <component :is="Component"/>
    </Transition>
  </RouterView>
  <SiteFooter :chair="chair"/>
  <Gdpr/>
</template>

<script lang="ts">
import {Gdpr} from '@/components/blocks'
import {SiteNavigation} from '@/components/navigation'
import {SiteFooter} from '@/components/sections'
import {useChairStore, usePageStore} from '@/stores'

export default {
  name: 'App',
  components: {Gdpr, SiteNavigation, SiteFooter},
  data() {
    return {
      chair: null as Chair | null,
      pages: {} as Record<template, Page>,
    }
  },
  async mounted() {
    await this.init()
    this.$nextTick(() => {
      this.updateDocumentTitle()
    })
  },
  methods: {
    async init() {
      this.chair = await useChairStore().get()
      if (!this.chair) return

      this.updateThemeColors(this.chair.colors.main, this.chair.colors.accent)
      this.pages = await usePageStore().all()
      await this.prefetchMandatoryPages()
      this.updateFavicon(this.chair.favicon)
    },
    async prefetchMandatoryPages() {
      const pageStore = usePageStore()

      for (const template of ['home', 'not_found', 'policies']) {
        await pageStore.get(template as template)
      }
    },
    updateFavicon(favicon: Media | null) {
      if (!favicon) return

      const element = document.getElementById('favicon') as HTMLLinkElement
      if (!element) return

      element.type = favicon.mime_type
      element.href = favicon.original
    },
    updateThemeColors(main_color: string, accent_color: string) {
      document.documentElement.style.setProperty('--main-color', main_color)
      document.documentElement.style.setProperty('--accent-color', accent_color)
    },
    updateDocumentTitle() {
      const meta = this.$route.meta
      if (!meta) return

      const locale = this.$i18n.locale as 'fr' | 'en'

      let titleKey = locale === 'en' && meta.titleEn
        ? meta.titleEn
        : locale === 'fr' && meta.titleFr
          ? meta.titleFr
          : meta.title

      if (!titleKey) return

      const keyStr = String(titleKey)
      const title = keyStr.includes('.') ? this.$t(keyStr) : keyStr
      document.title = `${title} | ${this.$t('site_title')}`
    }
  },
  watch: {
    '$i18n.locale': {
      async handler() {
        await this.init()
        this.updateDocumentTitle()
      }
    },
    '$route': {
      handler() {
        this.updateDocumentTitle()
      }
    }
  }
}
</script>
